import React, { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw';
import { Stack, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TaskAlt from '@mui/icons-material/TaskAlt';
import { Download, RadioButtonUncheckedOutlined } from '@mui/icons-material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import {
  SentimentSatisfiedAlt,
  Favorite,
  ThumbUp,
  ThumbDown,
  Star,
  Warning,
  CheckCircle,
  Error,
  EmojiEmotions,
  Lightbulb,
  Info,
  Help,
  BugReport,
  Security,
  Speed,
  TrendingUp,
  TrendingDown,
  AutoGraph,
  DataExploration,
  AutoStories
} from '@mui/icons-material';

import { getServerBaseUrl } from '@lib/utils/environmentUtils';
import { EventBusService } from '@lib/services/eventBusService';
import { blue, grey } from '@mui/material/colors';
import { useAiChatServices } from '../AiChat/AiChatContext';
import { themeTokens } from '@lib/themes/v2';

/**
 * Walks a React node tree and concatenates every string/number leaf
 * into plain text. Necessary because `node.toString()` on an array of
 * elements (which is what react-markdown hands the `a` renderer for
 * `[label](href)`) returns the literal string `"[object Object]"`.
 */
function reactNodeToText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactNodeToText).join('');
  if (React.isValidElement(node)) {
    const children = (node.props as { children?: React.ReactNode }).children;
    return reactNodeToText(children);
  }
  return '';
}

let SyntaxHighlighter: any = null;
let HighlighterStyle: any = null;

if (typeof window !== 'undefined') {
  Promise.all([
    import('react-syntax-highlighter/dist/esm/prism-light'),
    import('react-syntax-highlighter/dist/esm/styles/prism/one-dark'),
    import('react-syntax-highlighter/dist/esm/languages/prism/python'),
    import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
    import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
    import('react-syntax-highlighter/dist/esm/languages/prism/sql'),
    import('react-syntax-highlighter/dist/esm/languages/prism/json'),
    import('react-syntax-highlighter/dist/esm/languages/prism/markdown'),
    import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
    import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
    import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
    import('react-syntax-highlighter/dist/esm/languages/prism/css'),
    import('react-syntax-highlighter/dist/esm/languages/prism/csharp'),
    import('react-syntax-highlighter/dist/esm/languages/prism/yaml'),
  ]).then(([prismLight, style, markdown, markup, python, javascript, typescript, sql, json, bash, css, csharp, yaml]) => {
    SyntaxHighlighter = prismLight.default;
    HighlighterStyle = style.default;
    SyntaxHighlighter.registerLanguage('python', python.default);
    SyntaxHighlighter.registerLanguage('markdown', markdown.default);
    SyntaxHighlighter.registerLanguage('javascript', javascript.default);
    SyntaxHighlighter.registerLanguage('typescript', typescript.default);
    SyntaxHighlighter.registerLanguage('sql', sql.default);
    SyntaxHighlighter.registerLanguage('json', json.default);
    SyntaxHighlighter.registerLanguage('bash', bash.default);
    SyntaxHighlighter.registerLanguage('shell', bash.default);
    SyntaxHighlighter.registerLanguage('html', markup.default);
    SyntaxHighlighter.registerLanguage('xml', markup.default);
    SyntaxHighlighter.registerLanguage('css', css.default);
    SyntaxHighlighter.registerLanguage('csharp', csharp.default);
    SyntaxHighlighter.registerLanguage('yaml', yaml.default);
  }).catch((err) => console.error('Failed to load syntax highlighter:', err));
}

const rootImageUrl = `${getServerBaseUrl()}/open/getImage`;

interface MarkdownExtendedProps {
  text: string;
  sessionId: string;
  preStyle?: React.CSSProperties | null;
  onDrawCodeBlock?: (code: string, className: string) => React.ReactNode;
  onLinkClicked?: (link: string, content: string | null) => void;
  mode?: 'compact' | 'full',
  readingMode?: boolean
  partial?: boolean
  /**
   * Heading-style variant.
   * - `'classic'` (default): bold headings in `theme.palette.primary.main`
   *   with a colored bottom border. Matches the v1 dashboard-app look.
   * - `'minimal'`: subtler text-color headings with no border, smaller
   *   font sizes — for chat / inline content where headings shouldn't
   *   compete with the surrounding chrome (used by the v2 layout).
   */
  variant?: 'classic' | 'minimal'
}

/**
 * Lets a parent subtree opt every nested `MarkdownExtended` into the
 * minimal heading style without prop-drilling. The explicit `variant`
 * prop on `MarkdownExtended` always wins.
 */
export interface MarkdownExtendedDefaults {
  variant?: 'classic' | 'minimal';
}
export const MarkdownExtendedContext = createContext<MarkdownExtendedDefaults>({});
export const useMarkdownExtendedDefaults = (): MarkdownExtendedDefaults =>
  useContext(MarkdownExtendedContext);

// Icon mapping component
const InlineIcon: React.FC<{ iconCode: string }> = ({ iconCode }) => {
  const iconMap: Record<string, React.ReactElement> = {
    'smile': <SentimentSatisfiedAlt fontSize="small" />,
    'sad': <SentimentSatisfiedAlt fontSize="small" />,
    'thumbsup': <ThumbUp fontSize="small" />,
    'thumbsdown': <ThumbDown fontSize="small" />,
    'heart': <Favorite fontSize="small" />,
    'star': <Star fontSize="small" />,
    'warning': <Warning fontSize="small" />,
    'check': <CheckCircle fontSize="small" />,
    'x': <Error fontSize="small" />,
    'emoji': <EmojiEmotions fontSize="small" color="primary" />,
    'lightbulb': <Lightbulb fontSize="small" />,
    'info': <Info fontSize="small" color="info" />,
    'help': <Help fontSize="small" color="info" />,
    'bug': <BugReport fontSize="small" />,
    'security': <Security fontSize="small" />,
    'speed': <Speed fontSize="small" color="primary" />,
    'trending_up': <TrendingUp fontSize="small" />,
    'trending_down': <TrendingDown fontSize="small" />,
    'autograph': <AutoGraph fontSize="small" />,
    'dataobject': <AutoStories fontSize="small" />,
    'dataexploration': <DataExploration fontSize="small" />,
    'autostories': <AutoStories fontSize="small" />,
  };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
      marginTop: '-4px'
    }}>
      {iconMap[iconCode.toLowerCase()] || <span>:{iconCode}:</span>}
    </span>
  );
};

const MarkdownExtended: React.FC<MarkdownExtendedProps> = ({ text, sessionId, preStyle: _propPreStyle, onLinkClicked, partial, mode = 'full', variant: variantProp }) => {
  const ctxDefaults = useMarkdownExtendedDefaults();
  // v2 is the default surface — minimal headings (no amber/blue tint,
  // no underline, smaller font sizes). Legacy v1 surfaces opt back to
  // 'classic' via the prop or context.
  const variant = variantProp ?? ctxDefaults.variant ?? 'minimal';
  const theme = useTheme();

  let processedText = text;

  const { eventBusService } = useAiChatServices();

  const componentsFull: any = {
    span: ({ children, className, ...props }: any) => {
      if (className === 'inline-icon' && props['data-icon']) {
        return <InlineIcon iconCode={props['data-icon']} />;
      }
      return <span className={className} {...props}>{children}</span>;
    },
    img: ({ children, src, ...props }: any) => {
      return <div style={{ width: '100%', padding: '5px', margin: '16px 2px', borderRadius: '6px', border: '1px solid #333' }}><img src={`${rootImageUrl}/${src}`} {...props} style={{ width: '100%' }} /></div>
    },
    p: ({ children }: { children: React.ReactNode }) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
    input: ({ checked, disabled, ...props }: any) => {
      // Check if this is a checkbox (task list item)
      if (props.type === 'checkbox') {
        return checked ? (
          <TaskAlt
            sx={{
              verticalAlign: 'middle',
              marginRight: '4px',
              fontSize: 'inherit',
              color: theme.palette.primary.main,
              marginTop: '-2px'
            }}
          />
        ) : (
          <RadioButtonUncheckedOutlined
            sx={{
              verticalAlign: 'middle',
              marginRight: '4px',
              fontSize: 'inherit',
              color: theme.palette.text.secondary,
              marginTop: '-2px'
            }}
          />
        );
      }
      return <input checked={checked} disabled={disabled} {...props} />;
    },

    a: ({ children, href }: { children: React.ReactNode; href?: string }) =>
      <Stack direction="row" style={{ cursor: 'pointer', display: 'inline' }}>
        <a
          href={href}
          style={{ color: "var(--text-secondary)", textDecoration: 'none' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onLinkClicked)
              // `children` is a React node — calling `.toString()` on
              // an array of nodes (or any element) yields the literal
              // string "[object Object]", which then gets shoved into
              // the prompt input when this fires for `#ask-...` links.
              // Walk the tree and pull out every string/number leaf
              // so the host receives the link's actual text content.
              onLinkClicked(href ?? "", reactNodeToText(children) || null);
          }}>
          <div style={{ display: 'inline', alignItems: 'left', verticalAlign: 'middle', textWrap: 'wrap', color: theme.palette.text.primary, textDecorationColor: theme.palette.secondary.main }}>
            {children}
            {href?.startsWith("#") ?
              <OpenInNewIcon sx={{ position: 'relative', top: "3px", fontSize: '16px', marginLeft: '4px', marginTop: '-2px', color: theme.palette.secondary.main }} />
              :
              <Download sx={{ position: 'relative', top: "3px", fontSize: '16px', marginLeft: '4px', marginTop: '-2px', color: theme.palette.secondary.main }} />
            }

          </div>
        </a>
      </Stack>
    ,
    strong: ({ children }: { children: React.ReactNode }) => <strong style={{ fontWeight: 'bold', marginLeft: 1, marginRight: 1 }}>{children}</strong>,
    // ul: ({ children }: { children: React.ReactNode }) => <ul className="fa-ul">{children}</ul>,
    // li: ({ children }: { children: React.ReactNode }) => <li><span className="fa-li"><i className="fa-solid fa-pen-to-square"></i></span>{children}</li>,
    // ol: ({ children }: { children: React.ReactNode }) => <ol className="fa-ol">{children}</ol>,
    code: ({ className, children }: { className?: string; children: React.ReactNode }) => {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : className ?? '';

      //let codeTag: React.ReactNode | null = null;
      if (!className) {
        className = "";
      }

      if (language) {
        if (partial) {
          // While streaming, skip Prism — its tokenizer re-tokenizes
          // the entire (growing) block on every chunk and is a top
          // offender for blocking the main thread between WS messages.
          // Match SyntaxHighlighter's `PreTag="div"` so a `<pre>` tag
          // doesn't appear and trip a reveal/animation rule somewhere
          // upstream — keep `white-space: pre-wrap` on the div so the
          // formatting holds.
          return (
            <div style={{
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
              fontSize: '0.9em',
              marginTop: '0px',
              padding: '0.5em 0.75em',
              borderRadius: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              color: theme.palette.text.primary,
              fontFamily: 'monospace',
            }}>
              {String(children)}
            </div>
          );
        }
        return getSyntaxHighlighter(children, language);
      }
      else {
        return <code style={{
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.default,
          padding: '2px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
          overflow: 'auto',
          textOverflow: 'ellipsis',
          maxWidth: '100%'
        }}>
          {children}
        </code>
      }
    },
    pre: ({ children }: { children: React.ReactNode }) => (
      <div style={{ display: 'inherit', flexWrap: 'inherit', gap: 'inherit', textWrap: 'inherit' }}>
        {children}
      </div>
    ),

    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote style={{
        alignSelf: 'flex-start',
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word',
        margin: '16px 0px',
        padding: '8px 4px 2px 8px',
        width: '100%',
        borderRadius: '6px',
        backgroundColor: theme.palette.surface.raised,
        fontStyle: 'italic'
      }}>
        <Stack direction={"row"} gap={2} sx={{ display: 'inline-flex', alignItems: "center", flexWrap: 'nowrap' }}>
          <FormatQuoteIcon sx={{ position: "relative", top: -20 }} />
          {children}
        </Stack>
      </blockquote>
    ),
  }

  const hStyleClassic: React.CSSProperties = { color: theme.palette.primary.main, maxWidth: '95%', borderBottom: `1px solid ${theme.palette.primary.main}`, fontSize: '1.5em', margin: '0 0 8px 0', fontWeight: 'bold' };
  const hStyleMinimal: React.CSSProperties = {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '1.05em',
    margin: '10px 0 4px 0',
    lineHeight: 1.3,
    display: 'block',
  };
  const hStyle = variant === 'minimal' ? hStyleMinimal : hStyleClassic;

  // Heading components added to componentsFull only in minimal mode —
  // in classic mode the chat falls back to the browser's default huge
  // h1/h2 styles (existing v1 behavior left untouched).
  const minimalHeadings = {};
  Object.assign(componentsFull, minimalHeadings);
  const componentsCompact: any = {
    input: ({ checked, disabled, ...props }: any) => {
      // Check if this is a checkbox (task list item)
      if (props.type === 'checkbox') {
        return checked ? (
          <TaskAlt
            sx={{
              verticalAlign: 'middle',
              marginRight: '4px',
              fontSize: 'inherit',
              color: theme.palette.primary.main,
              marginTop: '-2px'
            }}
          />
        ) : (
          <RadioButtonUncheckedOutlined
            sx={{
              verticalAlign: 'middle',
              marginRight: '4px',
              fontSize: 'inherit',
              color: theme.palette.text.secondary,
              marginTop: '-2px'
            }}
          />
        );
      }
      return <input checked={checked} disabled={disabled} {...props} />;
    },
    p: ({ children }: { children: React.ReactNode }) => <span style={{ margin: '0 0 0 0', display: 'inline' }}>{children}</span>,
    // h1: ({ children }: { children: React.ReactNode }) => <h1 style={{ color: theme.palette.primary.main, maxWidth: '95%', fontSize: '1.5em', margin: '0 0 8px 0', fontWeight: 'bold' }}>{children}</h1>,
    // h2: ({ children }: { children: React.ReactNode }) => <h1 style={{ color: theme.palette.primary.main, maxWidth: '95%', fontSize: '1.5em', margin: '0 0 8px 0', fontWeight: 'bold' }}>{children}</h1>,
    // h3: ({ children }: { children: React.ReactNode }) => hStyle ? <span style={hStyle}>{children}</span> : <h3>{children}</h3>,
    // h4: ({ children }: { children: React.ReactNode }) => hStyle ? <span style={hStyle}>{children}</span> : <h4>{children}</h4>,
    // h5: ({ children }: { children: React.ReactNode }) => hStyle ? <span style={hStyle}>{children}</span> : <h5>{children}</h5>,
    // h6: ({ children }: { children: React.ReactNode }) => hStyle ? <span style={hStyle}>{children}</span> : <h6>{children}</h6>,
    ul: ({ children }: { children: React.ReactNode }) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
    ol: ({ children }: { children: React.ReactNode }) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
    li: ({ children }: { children: React.ReactNode }) => <li style={{ margin: '4px 0' }}>{children}</li>,
    strong: ({ children }: { children: React.ReactNode }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
    em: ({ children }: { children: React.ReactNode }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
  }

  const components = (mode === 'compact') ? componentsCompact : componentsFull;
  text = mode === 'compact' ? text.substring(0, 400) : text;

  const getSyntaxHighlighter = (children: React.ReactNode, language: string) => {
    if (!SyntaxHighlighter) {
      return <code style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>{String(children)}</code>;
    }
    return (
      <SyntaxHighlighter
        style={HighlighterStyle || {}}
        language={language}
        customStyle={{
          whiteSpace: 'pre-wrap',
          overflow: 'auto',
          fontSize: '0.9em',
          marginTop: '0px',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
        PreTag="div"
      >
        {String(children)}
      </SyntaxHighlighter>
    );
  }

  return (
    <ReactMarkdown
      skipHtml={false}
      urlTransform={(url) => {
        return url;
      }}
      // components={{
      //   a: () => null;
      // }}
      components={components}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
    >
      {processedText}
    </ReactMarkdown >
  );
};

export default MarkdownExtended;